import { inject } from "aurelia-framework";
import { I18N } from "aurelia-i18n";
import { PDFContent, PDFReportDefinition, PDFStyle } from "pdfmake-browserify";
import { ProductfinderResult, Pumpe } from "../productfinder-result";
import {
  Berechnungsart,
  Anwendung,
  TypViskositaet,
} from "../productfinder-data";
import { LocaleService } from "../../../services/locale-service";
import { BooleanValueConverter } from "../../../converters/boolean";

@inject(I18N, LocaleService)
export class KrachtPumpenReport {
  constructor(
    private i18n: I18N,
    private locl: LocaleService,
  ) {}

  private krachtLogo = require("./kracht-logo.png");

  createDefinition(
    result: ProductfinderResult,
    pumpeNr: number,
    imagePumpe?: string,
  ): PDFReportDefinition {
    let tr = (k) => this.i18n.tr(k);
    let trp = (k) => tr("productfinder." + k);
    let trpi = (k) => tr("productfinder.input." + k);
    let trpo = (k) => tr("productfinder.output." + k);

    let input = result.input!;

    // The server sends enums such as "Berechnungsart" as string (which is good!), but Typescript expects enums
    // as numbers, so we must convert from string to number here. This is somewhat complicated:
    let berechnungsart: Berechnungsart =
      Berechnungsart[(<any>input.berechnungsart) as string];
    let anwendung: Anwendung = Anwendung[(<any>input.anwendung) as string];
    let typViskositaet: TypViskositaet =
      TypViskositaet[(<any>input.typViskositaet) as string];

    let getPumpengroesse = (): PDFContent[] => {
      switch (berechnungsart) {
        case Berechnungsart.pumpe_auslegen:
        default:
          return [
            { text: trpi("q_foerderstrom"), bold: true },
            {
              text: this.locl.formatFloat(input.gewuenschterFoerderstrom),
              alignment: "right",
            },
            {
              text: tr(
                "units.foerderstrom." + input.gewuenschterFoerderstromUnit,
              ),
            },
          ];
        case Berechnungsart.pumpe_nachrechnen:
          return [
            { text: trpi("pumpengroesse"), bold: true },
            { text: input.pumpentyp.text, colSpan: 2 },
            "",
          ];
      }
    };

    let getMedium = (): PDFContent[] => {
      if (input.manuelleViskositaet) {
        return [{ text: trpo("manuelleViskositaet"), colSpan: 2 }, ""];
      } else {
        switch (anwendung) {
          case Anwendung.schmieroelAnwendung:
          default:
            return [{ text: input.schmieroel.text, colSpan: 2 }, ""];
          case Anwendung.marinekraftstoffAnwendung:
            return [{ text: input.marinekraftstoff.text, colSpan: 2 }, ""];
        }
      }
    };

    let getMediumTemperaturOrViskositaet = (): PDFContent[][] => {
      if (input.manuelleViskositaet) {
        let content: PDFContent[][] = [
          [
            { text: trpi("betriebsviskositaet") },
            {
              text: this.locl.formatFloat(input.betriebsviskositaet),
              alignment: "right",
            },
            { text: tr("units.viskositaet." + input.betriebsviskositaetUnit) },
          ],
          [
            { text: trpi("startviskositaet") },
            {
              text: input.startviskositaetEnabled
                ? this.locl.formatFloat(input.startviskositaet)
                : "-",
              alignment: "right",
            },
            {
              text: input.startviskositaetEnabled
                ? tr("units.viskositaet." + input.startviskositaetUnit)
                : "",
            },
          ],
        ];
        if (typViskositaet == TypViskositaet.dynamisch) {
          content.push([
            { text: trpi("dichte") },
            { text: this.locl.formatFloat(input.dichte), alignment: "right" },
            { text: tr("units.dichte." + input.dichteUnit) },
          ]);
        }
        return content;
      } else {
        return [
          [
            { text: trpi("betriebstemperatur") },
            {
              text: this.locl.formatFloat(input.betriebstemperatur),
              alignment: "right",
            },
            { text: tr("units.temperatur." + input.betriebstemperaturUnit) },
          ],
          [
            { text: trpi("anfahrtemperatur") },
            {
              text: input.anfahrtemperaturEnabled
                ? this.locl.formatFloat(input.anfahrtemperatur)
                : "-",
              alignment: "right",
            },
            {
              text: input.anfahrtemperaturEnabled
                ? tr("units.temperatur." + input.anfahrtemperaturUnit)
                : "",
            },
          ],
        ];
      }
    };

    let getEingabedatenTableBody = (): PDFContent[][] => {
      let tableBody: PDFContent[][] = [
        [
          { text: trpo("berechnungsart") },
          { text: trpo(input.berechnungsart), colSpan: 2 },
          "",
        ],
        [
          { text: trpi("anwendung") },
          { text: trpi(input.anwendung), colSpan: 2 },
          "",
        ],
      ];
      if (anwendung == Anwendung.schmieroelAnwendung) {
        tableBody = tableBody.concat([
          [
            { text: trpi("geraeuschoptimiert") },
            {
              text: new BooleanValueConverter(this.locl).toView(
                input.geraeuschoptimiert,
              ),
              colSpan: 2,
            },
            "",
          ],
        ]);
      }
      tableBody = tableBody.concat([
        [...getPumpengroesse()],
        [
          { text: trpi("betriebsdruck") },
          {
            text: this.locl.formatFloat(input.betriebsdruck),
            alignment: "right",
          },
          { text: tr("units.druck." + input.betriebsdruckUnit) },
        ],
      ]);
      if (
        berechnungsart == Berechnungsart.pumpe_nachrechnen ||
        (anwendung == Anwendung.schmieroelAnwendung && !input.netzfrequenz)
      ) {
        tableBody = tableBody.concat([
          [
            { text: trpi("betriebsdrehzahl") },
            {
              text:
                input.betriebsdrehzahl > 0
                  ? this.locl.formatInt(input.betriebsdrehzahl)
                  : "",
              alignment: "right",
            },
            { text: tr("units.drehzahl." + input.betriebsdrehzahlUnit) },
          ],
        ]);
      }
      if (
        berechnungsart == Berechnungsart.pumpe_auslegen &&
        input.netzfrequenz
      ) {
        tableBody = tableBody.concat([
          [
            { text: trpi("netzfrequenz") },
            {
              text: this.locl.formatInt(input.netzfrequenz),
              alignment: "right",
            },
            { text: "Hz" },
          ],
        ]);
      }
      tableBody = tableBody.concat([
        [{ text: trpo("medium"), bold: true }, ...getMedium()],
        ...getMediumTemperaturOrViskositaet(),
      ]);
      // if (anwendung == Anwendung.schmieroelAnwendung) {
      //     let note = trp("notes.input.schmieroel.geraeuschoptimiert");
      //     if (note) {
      //         tableBody = tableBody.concat([[{text: note, colSpan: 3, style: "smallInfo"}, '', '']]);
      //     }
      // }
      return tableBody;
    };

    let getBerechnungTableBody = (pumpe: Pumpe): PDFContent[][] => {
      let hasStartviskositaet =
        (input.manuelleViskositaet && input.startviskositaetEnabled) ||
        (!input.manuelleViskositaet && input.anfahrtemperaturEnabled);
      let content: PDFContent[][] = [
        [
          { text: trpi("pumpengroesse"), bold: true },
          { text: pumpe.pumpentyp.text, bold: true, colSpan: 2 },
          "",
        ],
        [
          { text: trpo("pump.foerderstrom"), bold: true },
          {
            text: this.locl.formatFloat(pumpe.foerderstrom),
            alignment: "right",
          },
          { text: tr("units.foerderstrom." + pumpe.foerderstromUnit) },
        ],
        [
          { text: trpo("pump.foerdervolumen") },
          {
            text: this.locl.formatFloat(pumpe.geomFoerdervolumen),
            alignment: "right",
          },
          { text: "cm³/r" },
        ],
        [
          { text: trpo("pump.drehzahl") },
          { text: this.locl.formatInt(pumpe.drehzahl), alignment: "right" },
          { text: tr("units.drehzahl.rpm") },
        ],
        [
          { text: trpi("betriebsviskositaet") },
          {
            text: this.locl.formatFloat(
              result.viskositaetWurdeBerechnet
                ? result.berechneteBetriebsviskositaet!
                : input.betriebsviskositaet,
            ),
            alignment: "right",
          },
          {
            text: tr(
              "units.viskositaet." +
                (result.viskositaetWurdeBerechnet
                  ? result.berechneteBetriebsviskositaetUnit!
                  : input.betriebsviskositaetUnit),
            ),
          },
        ],
        [
          { text: trpi("startviskositaet") },
          {
            text: hasStartviskositaet
              ? this.locl.formatFloat(
                  result.viskositaetWurdeBerechnet
                    ? result.berechneteStartviskositaet!
                    : input.startviskositaet,
                )
              : "-",
            alignment: "right",
          },
          {
            text: hasStartviskositaet
              ? tr(
                  "units.viskositaet." +
                    (result.viskositaetWurdeBerechnet
                      ? result.berechneteStartviskositaetUnit!
                      : input.startviskositaetUnit),
                )
              : "",
          },
        ],
        // [{text: trpo('pump.schalldruckpegel')}, {text: '', alignment: 'right'}, {text: 'dB(A)'}],
        // [{text: trpo('pump.npshr')}, {text: '', alignment: 'right'}, {text: 'm'}],
        [
          { text: trpo("pump.leistungsbedarf"), bold: true },
          {
            text: this.locl.formatFloat(pumpe.leistungsbedarf),
            alignment: "right",
          },
          { text: tr("units.leistung." + pumpe.leistungUnit) },
        ],
        [
          { text: trpo("pump.drehmoment"), bold: true },
          { text: this.locl.formatFloat(pumpe.drehmoment), alignment: "right" },
          { text: tr("units.drehmoment." + pumpe.drehmomentUnit) },
        ],
      ];
      if (pumpe.mitGeraeuschrisiko) {
        content.push([
          { text: trpo("pump.mitGeraeuschrisiko"), colSpan: 3 },
          "",
          "",
        ]);
      }
      let note = trp("notes.output");
      if (note) {
        content.push([{ text: note, colSpan: 3, style: "smallInfo" }, "", ""]);
      }
      return content;
    };

    // now follows the overall report definition

    let definition: PDFReportDefinition = {
      info: {
        title: trp("report.title"),
        author: trp("report.author"),
        creationDate: new Date().toString(),
      },
      pageSize: "A4",
      pageMargins: { top: 150, left: 40, right: 40, bottom: 40 },
      header: function () {
        return [
          // header must be a function, not a static array because of https://github.com/bpampuch/pdfmake/issues/671
          { image: "krachtLogo", alignment: "right", margin: [0, 40, 20, 10] },
          {
            canvas: [
              {
                type: "line",
                x1: 40,
                y1: 0,
                x2: 560,
                y2: 0,
                lineWidth: 0.5,
                lineColor: "#777",
              },
            ],
          },
        ];
      },
      content: [
        { text: trp("report.ourRecommendation") },
        { text: trpo("inputData"), style: "header" },
        {
          table: {
            widths: [150, 50, 200],
            body: getEingabedatenTableBody(),
          },
          layout: {
            fillColor: "#eee",
            defaultBorder: true,
            hLineColor: "#e0e0e0",
            vLineColor: "#e0e0e0",
          },
        },
        { text: trpo("result"), style: "header" },
        {
          table: {
            widths: [150, 50, 200],
            body: getBerechnungTableBody(result.pumpen![pumpeNr]),
          },
          layout: {
            fillColor: "#eee",
            defaultBorder: true,
            hLineColor: "#e0e0e0",
            vLineColor: "#e0e0e0",
          },
        },
        {
          text: trp("report.disclaimer1") + "\n" + trp("report.disclaimer2"),
          style: "info",
        },
        { text: trp("report.remarks"), style: "notizen" },
        { canvas: [{ type: "rect", x: 0, y: 0, w: 520, h: 60 }] },
      ],
      footer: {
        text: trp("report.footer"),
        style: "footer",
      },
      images: {
        krachtLogo: this.krachtLogo,
      },
      styles: {
        header: <PDFStyle>{
          fontSize: 12,
          bold: true,
          margin: [/*left*/ 0, /*top*/ 30, /*right*/ 0, /*bottom*/ 10],
        },
        smallInfo: <PDFStyle>{
          fontSize: 9,
          color: "#777",
          margin: [/*horizontal*/ 0, /*vertical*/ 0],
        },
        info: <PDFStyle>{
          fontSize: 9,
          color: "#777",
          margin: [/*horizontal*/ 0, /*vertical*/ 20],
        },
        notizen: <PDFStyle>{ fontSize: 9, margin: [0, 30, 0, 0] },
        footer: <PDFStyle>{ fontSize: 8, margin: [36, 0, 36, 0] },
      },
      defaultStyle: {
        fontSize: 10,
        color: "#444",
        //font: 'kracht-font' // TODO: right now the report uses default font "Roboto", use KRACHT font
      },
    };

    let content = <PDFContent[]>definition.content;
    if (imagePumpe) {
      content.push({ image: imagePumpe, width: 500, pageBreak: "before" });
      content.push({
        text: trp("report.disclaimer1") + "\n" + trp("report.disclaimer2"),
        style: "info",
      });
    }

    return definition;
  }
}
